import { db, storage } from "../../firebase";
import {
  Timestamp,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";

export const createNewNewsletter = async ({
  data,
  collectionName = "Newsletter",
}) => {
  if (!data?.publicationName) {
    throw new Error("Publication Name is undefined");
  }
  if (!data?.articleTitle) {
    throw new Error("Article Title is undefined");
  }
  if (!data?.volume) {
    throw new Error("Volume is undefined or empty");
  }
  if (!data?.daysOfPublication) {
    throw new Error("Days of Publication is undefined");
  }
  if (!data?.pdfFile) {
    throw new Error("PDF File is undefined");
  }

  // Ensure volume is not empty or just whitespace
  if (data.volume.trim() === "") {
    throw new Error("Volume cannot be empty or whitespace");
  }

  const id =
    data?.id ||
    `${data.publicationName.replace(/\s+/g, "-").toLowerCase()}-${data.volume
      .replace(/\s+/g, "-")
      .toLowerCase()}`;
  // console.log("Generated ID for new newsletter:", id);

  // Create document reference
  const firestoreRef = doc(db, collectionName, id);

  try {
    // Upload PDF to Storage
    const pdfRef = ref(storage, `${collectionName}/${id}.pdf`);
    await uploadBytes(pdfRef, data.pdfFile);
    const pdfUrl = await getDownloadURL(pdfRef);

    // Save newsletter document, excluding pdfFile
    const { pdfFile, ...dataToSave } = data; // Destructure to exclude pdfFile
    await setDoc(firestoreRef, {
      ...dataToSave,
      id,
      productType: "Newsletter",
      timestamp: data?.timestamp || Timestamp.now(),
      pdfUrl,
    });
    // console.log("Newsletter created with ID:", id);
  } catch (error) {
    console.error("Firestore Write Error:", error);
    throw new Error(`Failed to create newsletter: ${error.message}`);
  }
};

export const updateNewsletter = async ({
  data,
  collectionName = "Newsletter",
}) => {
  if (!data?.id) {
    throw new Error("ID is undefined for update");
  }
  if (!data?.publicationName) {
    throw new Error("Publication Name is undefined");
  }
  if (!data?.articleTitle) {
    throw new Error("Article Title is undefined");
  }
  if (!data?.volume) {
    throw new Error("Volume is undefined or empty");
  }
  if (!data?.daysOfPublication) {
    throw new Error("Days of Publication is undefined");
  }

  // Ensure volume is not empty or just whitespace
  if (data.volume.trim() === "") {
    throw new Error("Volume cannot be empty or whitespace");
  }

  const firestoreRef = doc(db, collectionName, data.id);

  try {
    // Upload new PDF if provided
    let pdfUrl = data.pdfUrl;
    if (data.pdfFile) {
      const pdfRef = ref(storage, `${collectionName}/${data.id}.pdf`);
      await uploadBytes(pdfRef, data.pdfFile);
      pdfUrl = await getDownloadURL(pdfRef);
    }

    // Update newsletter document, excluding pdfFile
    const { pdfFile, ...dataToSave } = data; // Destructure to exclude pdfFile
    await updateDoc(firestoreRef, {
      ...dataToSave,
      productType: "Newsletter",
      timestamp: data?.timestamp || Timestamp.now(),
      pdfUrl,
    });
    // console.log("Newsletter updated with ID:", data.id);
  } catch (error) {
    console.error("Firestore Update Error:", error);
    throw new Error(`Failed to update newsletter: ${error.message}`);
  }
};

export const deleteNewsletter = async (id, collectionName = "Newsletter") => {
  if (!id) {
    throw new Error("Id is required");
  }

  try {
    // Delete PDF from Storage
    try {
      const pdfRef = ref(storage, `${collectionName}/${id}.pdf`);
      await deleteObject(pdfRef);
    } catch (error) {
      if (error.code !== "storage/object-not-found") {
        throw error;
      }
    }

    // Delete newsletter document
    await deleteDoc(doc(db, collectionName, id));
    // console.log("Newsletter deleted with ID:", id);
  } catch (error) {
    console.error("Firestore Delete Error:", error);
    throw new Error(`Failed to delete newsletter: ${error.message}`);
  }
};
